import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Tag,
    Space,
    message,
    Popconfirm,
    Row,
    Col,
    Statistic,
    Typography,
    Divider,
    Badge,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SendOutlined,
    ReloadOutlined,
    StopOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import api from '../../../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Notifications = () => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingNotification, setEditingNotification] = useState(null);
    const [form] = Form.useForm();
    const [stats, setStats] = useState({});
    const [filters, setFilters] = useState({});

    // Check if user has admin role
    if (!user || user.user_type !== 'admin') {
        return (
            <div style={{ padding: '50px', textAlign: 'center' }}>
                <h2>Access Denied</h2>
                <p>You don't have permission to access this page.</p>
            </div>
        );
    }

    useEffect(() => {
        fetchNotifications();
        fetchStats();
    }, [filters]);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await api.adminGetNotifications(filters);
            setNotifications(response.data.data);
        } catch (error) {
            message.error('Failed to fetch notifications');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.adminGetNotificationStats();
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const handleCreate = () => {
        setEditingNotification(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (notification) => {
        setEditingNotification(notification);
        form.setFieldsValue({
            ...notification,
            scheduled_at: notification.scheduled_at ? dayjs(notification.scheduled_at) : null,
        });
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        try {
            const payload = {
                ...values,
                scheduled_at: values.scheduled_at ? values.scheduled_at.toISOString() : null,
            };

            if (editingNotification) {
                await api.adminUpdateNotification(editingNotification.id, payload);
                message.success('Notification updated successfully');
            } else {
                await api.adminCreateNotification(payload);
                message.success('Notification created successfully');
            }
            setModalVisible(false);
            fetchNotifications();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to save notification');
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.adminDeleteNotification(id);
            message.success('Notification deleted successfully');
            fetchNotifications();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to delete notification');
        }
    };

    const handleSend = async (id) => {
        try {
            await api.adminSendNotification(id);
            message.success('Notification sent successfully');
            fetchNotifications();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to send notification');
        }
    };

    const handleRetry = async (id) => {
        try {
            await api.adminRetryNotification(id);
            message.success('Notification retry initiated');
            fetchNotifications();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to retry notification');
        }
    };

    const handleCancel = async (id) => {
        try {
            await api.adminCancelNotification(id);
            message.success('Notification cancelled');
            fetchNotifications();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to cancel notification');
        }
    };

    const handleProcessScheduled = async () => {
        try {
            await api.adminProcessScheduledNotifications();
            message.success('Scheduled notifications processed');
            fetchNotifications();
            fetchStats();
        } catch (error) {
            message.error(error.message || 'Failed to process scheduled notifications');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'sent': return 'success';
            case 'pending': return 'processing';
            case 'failed': return 'error';
            case 'cancelled': return 'default';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'sent': return <CheckCircleOutlined />;
            case 'pending': return <ClockCircleOutlined />;
            case 'failed': return <CloseCircleOutlined />;
            case 'cancelled': return <StopOutlined />;
            default: return null;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'email': return 'blue';
            case 'sms': return 'green';
            case 'push': return 'purple';
            case 'in_app': return 'orange';
            default: return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent': return 'red';
            case 'high': return 'orange';
            case 'normal': return 'blue';
            case 'low': return 'green';
            default: return 'default';
        }
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (text, record) => (
                <div>
                    <Text strong>{text}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.message?.substring(0, 50)}...
                    </Text>
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={getTypeColor(type)}>
                    {type.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            render: (priority) => (
                <Tag color={getPriorityColor(priority)}>
                    {priority?.toUpperCase() || 'NORMAL'}
                </Tag>
            ),
        },
        {
            title: 'Recipient',
            key: 'recipient',
            render: (_, record) => (
                <div>
                    <Text>{record.recipient_type || 'All'}</Text>
                    {record.recipient_id && (
                        <Text type="secondary"> (ID: {record.recipient_id})</Text>
                    )}
                </div>
            ),
        },
        {
            title: 'Scheduled',
            dataIndex: 'scheduled_at',
            key: 'scheduled_at',
            render: (date) => date ? dayjs(date).format('MMM DD, YYYY HH:mm') : 'Immediate',
        },
        {
            title: 'Sent',
            dataIndex: 'sent_at',
            key: 'sent_at',
            render: (date) => date ? dayjs(date).format('MMM DD, YYYY HH:mm') : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {record.status === 'pending' && (
                        <Button
                            type="primary"
                            size="small"
                            icon={<SendOutlined />}
                            onClick={() => handleSend(record.id)}
                        >
                            Send
                        </Button>
                    )}
                    {record.status === 'failed' && (
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => handleRetry(record.id)}
                        >
                            Retry
                        </Button>
                    )}
                    {record.status === 'pending' && (
                        <Button
                            size="small"
                            icon={<StopOutlined />}
                            onClick={() => handleCancel(record.id)}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    {record.status !== 'sent' && (
                        <Popconfirm
                            title="Are you sure you want to delete this notification?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            >
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="mb-6">
                <Title level={2}>Notifications</Title>
                <Text type="secondary">
                    Manage email, SMS, push, and in-app notifications
                </Text>
            </div>

            {/* Statistics */}
            <Row gutter={16} className="mb-6">
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Total"
                            value={stats.total || 0}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Pending"
                            value={stats.pending || 0}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Sent"
                            value={stats.sent || 0}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Failed"
                            value={stats.failed || 0}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="Today"
                            value={stats.today || 0}
                        />
                    </Card>
                </Col>
                <Col span={4}>
                    <Card>
                        <Statistic
                            title="This Week"
                            value={stats.this_week || 0}
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <div className="mb-4 flex justify-between items-center">
                    <Title level={4} className="!mb-0">All Notifications</Title>
                    <Space>
                        <Button
                            icon={<ClockCircleOutlined />}
                            onClick={handleProcessScheduled}
                        >
                            Process Scheduled
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleCreate}
                        >
                            Create Notification
                        </Button>
                    </Space>
                </div>

                {/* Filters */}
                <div className="mb-4">
                    <Row gutter={16}>
                        <Col span={6}>
                            <Select
                                placeholder="Filter by type"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => setFilters({ ...filters, type: value })}
                            >
                                <Option value="email">Email</Option>
                                <Option value="sms">SMS</Option>
                                <Option value="push">Push</Option>
                                <Option value="in_app">In-App</Option>
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Select
                                placeholder="Filter by status"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => setFilters({ ...filters, status: value })}
                            >
                                <Option value="pending">Pending</Option>
                                <Option value="sent">Sent</Option>
                                <Option value="failed">Failed</Option>
                                <Option value="cancelled">Cancelled</Option>
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Select
                                placeholder="Filter by priority"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => setFilters({ ...filters, priority: value })}
                            >
                                <Option value="urgent">Urgent</Option>
                                <Option value="high">High</Option>
                                <Option value="normal">Normal</Option>
                                <Option value="low">Low</Option>
                            </Select>
                        </Col>
                        <Col span={6}>
                            <RangePicker
                                style={{ width: '100%' }}
                                onChange={(dates) => {
                                    if (dates) {
                                        setFilters({
                                            ...filters,
                                            date_from: dates[0]?.format('YYYY-MM-DD'),
                                            date_to: dates[1]?.format('YYYY-MM-DD'),
                                        });
                                    } else {
                                        setFilters({
                                            ...filters,
                                            date_from: undefined,
                                            date_to: undefined,
                                        });
                                    }
                                }}
                            />
                        </Col>
                    </Row>
                </div>

                <Table
                    columns={columns}
                    dataSource={notifications}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} notifications`,
                    }}
                />
            </Card>

            <Modal
                title={editingNotification ? 'Edit Notification' : 'Create Notification'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="type"
                        label="Type"
                        rules={[{ required: true, message: 'Please select type' }]}
                    >
                        <Select placeholder="Select notification type">
                            <Option value="email">Email</Option>
                            <Option value="sms">SMS</Option>
                            <Option value="push">Push</Option>
                            <Option value="in_app">In-App</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="title"
                        label="Title"
                        rules={[{ required: true, message: 'Please enter title' }]}
                    >
                        <Input placeholder="Notification title" />
                    </Form.Item>

                    <Form.Item
                        name="message"
                        label="Message"
                        rules={[{ required: true, message: 'Please enter message' }]}
                    >
                        <TextArea rows={4} placeholder="Notification message" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="priority"
                                label="Priority"
                                initialValue="normal"
                            >
                                <Select>
                                    <Option value="low">Low</Option>
                                    <Option value="normal">Normal</Option>
                                    <Option value="high">High</Option>
                                    <Option value="urgent">Urgent</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="scheduled_at"
                                label="Schedule"
                            >
                                <DatePicker
                                    showTime
                                    style={{ width: '100%' }}
                                    placeholder="Leave empty for immediate"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="recipient_type"
                                label="Recipient Type"
                                initialValue="all"
                            >
                                <Select>
                                    <Option value="all">All Users</Option>
                                    <Option value="user">Specific User</Option>
                                    <Option value="admin">Admins</Option>
                                    <Option value="vendor">Vendors</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="recipient_id"
                                label="Recipient ID"
                            >
                                <Input placeholder="User ID (if specific user)" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item className="mb-0">
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingNotification ? 'Update' : 'Create'}
                            </Button>
                            <Button onClick={() => setModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default Notifications;